import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, getCategory } from '@/utils/content';
import type { Article } from '@/types/content';

type SortKey = 'latest' | 'hot';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = getCategory(slug);
  const [sort, setSort] = useState<SortKey>('latest');
  const [activeTag, setActiveTag] = useState<string>('全部');

  const baseArticles = useMemo(() => getArticlesByCategory(slug), [slug]);

  const tags = useMemo(() => {
    const set = new Set<string>();
    baseArticles.forEach((article) => article.tags.forEach((tag) => set.add(tag)));
    return ['全部', ...Array.from(set)].slice(0, 8);
  }, [baseArticles]);

  const list = useMemo(() => {
    const filtered =
      activeTag === '全部'
        ? baseArticles
        : baseArticles.filter((article) => article.tags.includes(activeTag));
    return [...filtered].sort((a, b) => {
      if (sort === 'hot') return b.heat - a.heat;
      return a.date < b.date ? 1 : -1;
    });
  }, [baseArticles, activeTag, sort]);

  useSeo({
    title: category
      ? `${category.name}档案 | 美日吃瓜`
      : '栏目未找到 | 美日吃瓜',
    description: category
      ? `${category.description}美日吃瓜「${category.name}」栏目已整理 ${category.count} 篇可追溯来源的深度档案，持续更新。`
      : '该栏目暂不存在，请返回首页浏览其他内容栏目。',
    keywords: category
      ? `美日吃瓜,美日吃瓜${category.name},${category.name}调查,政经暗面`
      : '美日吃瓜',
    path: `/category/${slug}`,
    jsonLd: category
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'CollectionPage',
              name: `${category.name}档案`,
              url: `/category/${slug}`,
              inLanguage: 'zh-CN',
              description: category.description,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category.name,
                  item: `/category/${slug}`,
                },
              ],
            },
            {
              '@type': 'ItemList',
              name: `${category.name}档案列表`,
              itemListElement: baseArticles.map((article, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `/article/${article.slug}`,
                name: article.title,
              })),
            },
          ],
        }
      : undefined,
  });

  if (!category) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
          <i className="ri-file-unknow-line" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          没有找到这个栏目
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          你访问的栏目可能已经调整或不存在。可以回到首页，从六大栏目中重新选择。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs items={[{ label: category.name }]} />

      <section className="border-b border-background-300/70 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-100 px-3 py-1 font-label text-xs font-semibold text-primary-700">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={category.icon} />
                </span>
                内容栏目
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {category.name}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {category.description}
              </p>
            </div>
            <div className="flex items-baseline gap-2 lg:shrink-0">
              <span className="font-heading text-3xl font-bold text-primary-600">
                {category.count}
              </span>
              <span className="font-label text-sm text-foreground-500">篇存档中的档案</span>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 flex flex-col gap-4 border-b border-background-300/70 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors ${
                  activeTag === tag
                    ? 'bg-primary-600 text-background-50'
                    : 'border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="flex shrink-0 items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setSort('latest')}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 font-label text-xs transition-colors ${
                sort === 'latest'
                  ? 'bg-foreground-900 text-background-50'
                  : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              最新发布
            </button>
            <button
              type="button"
              onClick={() => setSort('hot')}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 font-label text-xs transition-colors ${
                sort === 'hot'
                  ? 'bg-foreground-900 text-background-50'
                  : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              按热度
            </button>
          </div>
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((article: Article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-background-300/70 bg-background-100 px-6 py-16 text-center">
            <p className="font-heading text-lg font-bold text-foreground-950">
              这个筛选条件下暂时没有稿件
            </p>
            <p className="mt-2 text-sm text-foreground-600">
              换一个标签或切换排序方式，也许会有新的档案出现。
            </p>
          </div>
        )}

        <section className="mt-14 border-t border-background-300/70 pt-8">
          <h2 className="mb-5 font-heading text-lg font-bold text-foreground-950">浏览其他栏目</h2>
          <div className="flex flex-wrap gap-3">
            {categories
              .filter((item) => item.slug !== category.slug)
              .map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                    <i className={item.icon} />
                  </span>
                  {item.name}
                </Link>
              ))}
          </div>
        </section>
      </div>
    </>
  );
}